import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import ScenarioStatus from './components/ScenarioStatus';
import QuantityWithUnit from './components/QuantityWithUnit';
import ConfirmationDialog from './components/ConfirmationDialog';
import { formatBangkokRange, formatBangkokDateTime } from './utils/bangkokTime';
import { completeBatch, startBatch } from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';

const BATCH_ACTION_BY_STATUS = {
  PLANNED: 'start',
  RELEASED: 'start',
  SCHEDULED: 'start',
  RUNNING: 'complete',
  IN_PROGRESS: 'complete',
};

// Execution detail for a single batch: lifecycle action appropriate to its *current* server
// status, actual-yield entry with validation, and a reconcile result rendered exactly as the
// backend returns it (linked / unmatched / ambiguous) rather than a client guess.
export default function BatchDetail({ batch, onChanged }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const [actualYield, setActualYield] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!batch) return <Typography color="text.secondary">Select a batch from the timeline.</Typography>;

  const nextAction = BATCH_ACTION_BY_STATUS[batch.status];

  const runStart = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await startBatch({ batchId: batch.id, accessToken });
      setMessage({ severity: 'success', text: `Batch is now ${response.data.status}.` });
      onChanged?.();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to start batch.').summary });
    } finally {
      setBusy(false);
    }
  };

  const runComplete = async () => {
    const parsed = Number(actualYield);
    if (!actualYield || !Number.isFinite(parsed) || parsed <= 0) {
      setMessage({ severity: 'error', text: 'Enter a valid actual yield (kg) greater than zero before completing this batch.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const response = await completeBatch({ batchId: batch.id, body: { actual_yield_kg: parsed }, accessToken });
      setMessage({ severity: 'success', text: `Batch completed. Server status: ${response.data.status}.` });
      onChanged?.();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to complete batch.').summary });
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
        <Box>
          <Typography variant="h6">Batch #{batch.id} — {batch.sku}</Typography>
          <Typography color="text.secondary">Pot {batch.pot_id} · Line {batch.line}</Typography>
        </Box>
        <ScenarioStatus status={batch.status} />
      </Stack>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mt: 2 }}>{message.text}</Alert>}

      <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
        <Field label="Planned" value={formatBangkokRange(batch.planned_start, batch.planned_end)} />
        <Field label="Ready" value={(batch.actual_ready_at || batch.planned_ready_at) ? formatBangkokDateTime((batch.actual_ready_at || batch.planned_ready_at)) : '—'} />
        <Field label="Due" value={batch.due_at ? formatBangkokDateTime(batch.due_at) : '—'} />
      </Stack>
      <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Planned yield</Typography>
          <QuantityWithUnit value={batch.planned_yield_kg} unit="kg" />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Actual yield</Typography>
          <QuantityWithUnit value={batch.actual_yield_kg} unit="kg" />
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {nextAction === 'start' && (
        <Button variant="contained" disabled={busy} onClick={runStart}>{busy ? 'Working…' : 'Start batch'}</Button>
      )}

      {nextAction === 'complete' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Actual yield (kg)"
            type="number"
            size="small"
            value={actualYield}
            onChange={(event) => setActualYield(event.target.value)}
          />
          <Button variant="contained" disabled={busy} onClick={() => setConfirmOpen(true)}>Complete batch</Button>
        </Stack>
      )}

      {!nextAction && (
        <Typography color="text.secondary">
          No lifecycle action available from status {batch.status}.
        </Typography>
      )}

      {Array.isArray(batch.reconcile_result) && batch.reconcile_result.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">Reconcile result</Typography>
          {batch.reconcile_result.map((entry, index) => (
            <Typography key={index} variant="body2" color={entry.status === 'ambiguous' ? 'warning.main' : 'text.primary'}>
              {entry.status}: {entry.detail || JSON.stringify(entry)}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary">
            Ambiguous matches are shown for manual review — the UI does not auto-select a barrel.
          </Typography>
        </>
      )}

      <ConfirmationDialog
        open={confirmOpen}
        title="Complete this batch?"
        description={`Recording ${actualYield || '0'} kg actual yield for batch #${batch.id}. This cannot be undone from here.`}
        confirmLabel="Complete"
        busy={busy}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={runComplete}
      />
    </Box>
  );
}

function Field({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}
